/**
 * Rigid 2D alignment (Kabsch/Umeyama, no scale) from a set of source-target
 * point pairs. Solves for yaw (rotation about the vertical axis) + planar
 * translation so that `R(yaw) * src + t ≈ tgt` in least-squares sense.
 *
 * Coordinates are the XZ plane (Y-up, matching RoomPlan and inspect_3d).
 */

export interface AlignResult {
  yaw: number;   // radians
  tx: number;
  tz: number;
  rmse: number;  // metres
}

export function kabsch2d(
  src: Array<[number, number]>,
  tgt: Array<[number, number]>,
): AlignResult {
  if (src.length !== tgt.length) throw new Error("kabsch2d: length mismatch");
  if (src.length < 2) throw new Error("kabsch2d: need at least 2 point pairs");

  const n = src.length;

  let sx = 0, sy = 0, tx = 0, ty = 0;
  for (let i = 0; i < n; i++) {
    sx += src[i][0]; sy += src[i][1];
    tx += tgt[i][0]; ty += tgt[i][1];
  }
  sx /= n; sy /= n; tx /= n; ty /= n;

  // Accumulate the 2×2 cross-covariance — yaw falls out of its angle.
  let num = 0; // Σ sdx * tdy - sdy * tdx  (cross)
  let den = 0; // Σ sdx * tdx + sdy * tdy  (dot)
  for (let i = 0; i < n; i++) {
    const sdx = src[i][0] - sx;
    const sdy = src[i][1] - sy;
    const tdx = tgt[i][0] - tx;
    const tdy = tgt[i][1] - ty;
    num += sdx * tdy - sdy * tdx;
    den += sdx * tdx + sdy * tdy;
  }

  const yaw = Math.atan2(num, den);
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);

  // t = tgt_centroid - R * src_centroid
  const txOut = tx - (cos * sx - sin * sy);
  const tzOut = ty - (sin * sx + cos * sy);

  // RMSE of the residual.
  let sumErr = 0;
  for (let i = 0; i < n; i++) {
    const px = cos * src[i][0] - sin * src[i][1] + txOut;
    const pz = sin * src[i][0] + cos * src[i][1] + tzOut;
    const dx = px - tgt[i][0];
    const dz = pz - tgt[i][1];
    sumErr += dx * dx + dz * dz;
  }
  const rmse = Math.sqrt(sumErr / n);

  return { yaw, tx: txOut, tz: tzOut, rmse };
}
